import React from 'react';
import { Link } from 'react-router-dom';
import { useMyEnrollments } from '../hooks/useCompetency';

const CompetencyDashboard = () => {
  const { enrollments, loading, error } = useMyEnrollments();
  const userRole = localStorage.getItem('userRole');
  const isClient = userRole === 'client';

  const getStatusBadge = (status) => {
    const styles = {
      enrolled: { bg: '#e3f2fd', color: '#1976d2', text: 'Enrolled' },
      in_progress: { bg: '#fff3e0', color: '#f57c00', text: 'In Progress' },
      assessment_pending: { bg: '#fce4ec', color: '#c2185b', text: 'Assessment Pending' },
      exam_pending: { bg: '#f3e5f5', color: '#7b1fa2', text: 'Exam Pending' },
      evidence_review: { bg: '#e0f2f1', color: '#00897b', text: 'Evidence Review' },
      assessor_review: { bg: '#fff9c4', color: '#f57f17', text: 'Assessor Review' },
      completed: { bg: '#c8e6c9', color: '#2e7d32', text: 'Completed' },
      failed: { bg: '#ffcdd2', color: '#c62828', text: 'Failed' }
    };

    const style = styles[status] || styles.enrolled;

    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {style.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <h1>My Professional Competency Courses</h1>
        <p>Loading your enrollments...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <h1>My Professional Competency Courses</h1>
        <div style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>{isClient ? 'My Assigned Courses' : 'My Professional Competency Courses'}</h1>
        {!isClient && (
          <Link
            to="/competency/courses"
            style={{
              background: '#D4AF37',
              color: '#fff',
              padding: '10px 20px',
              borderRadius: '4px',
              textDecoration: 'none',
              fontWeight: 'bold'
            }}
          >
            Browse Courses
          </Link>
        )}
      </div>

      {enrollments.length === 0 ? (
        <div style={{
          padding: '60px 40px',
          textAlign: 'center',
          background: '#f5f5f5',
          borderRadius: '8px',
          border: '2px dashed #ddd'
        }}>
          <h2 style={{ color: '#666', marginBottom: '15px' }}>No Courses Assigned Yet</h2>
          <p style={{ color: '#888', marginBottom: '25px', fontSize: '16px' }}>
            {isClient
              ? 'Your certification body has not yet assigned any courses. Please contact them for more information.'
              : 'Get started by enrolling in a professional competency course'}
          </p>
          {!isClient && (
            <Link
              to="/competency/courses"
              style={{
                display: 'inline-block',
                background: '#D4AF37',
                color: '#fff',
                padding: '12px 30px',
                borderRadius: '4px',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              Browse Available Courses
            </Link>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {enrollments.map(enrollment => (
            <div
              key={enrollment.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '25px',
                background: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '8px' }}>
                    <h2 style={{ margin: 0, fontSize: '22px' }}>
                      {enrollment.course_title}
                    </h2>
                    {getStatusBadge(enrollment.status)}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>
                    Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}
                    {enrollment.assessor_name && (
                      <span> • Assessor: {enrollment.assessor_name}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Progress</span>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {enrollment.completed_modules?.length || 0} / {enrollment.total_modules || '?'} modules
                  </span>
                </div>
                <div style={{
                  height: '10px',
                  background: '#e0e0e0',
                  borderRadius: '5px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    background: 'linear-gradient(90deg, #D4AF37, #B8860B)',
                    width: `${enrollment.progress_percentage || 0}%`,
                    transition: 'width 0.3s'
                  }} />
                </div>
                <div style={{ textAlign: 'right', marginTop: '5px', fontSize: '13px', color: '#666' }}>
                  {Math.round(enrollment.progress_percentage || 0)}% Complete
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                {enrollment.status === 'enrolled' || enrollment.status === 'in_progress' ? (
                  <Link
                    to={`/competency/learn/${enrollment.id}`}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#D4AF37',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textAlign: 'center',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    {enrollment.status === 'enrolled' ? 'Start Learning' : 'Continue Learning'}
                  </Link>
                ) : enrollment.status === 'assessment_pending' ? (
                  <Link
                    to={`/competency/assessment/${enrollment.id}`}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#2196f3',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textAlign: 'center',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    Submit Assessment
                  </Link>
                ) : enrollment.status === 'exam_pending' ? (
                  <Link
                    to={`/competency/exam/${enrollment.id}`}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#ff9800',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textAlign: 'center',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    Take Exam
                  </Link>
                ) : enrollment.status === 'evidence_review' ? (
                  <Link
                    to={`/competency/evidence/${enrollment.id}`}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#00897b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textAlign: 'center',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    Submit Evidence
                  </Link>
                ) : enrollment.status === 'completed' ? (
                  <Link
                    to={`/competency/certificate/${enrollment.id}`}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#2e7d32',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textAlign: 'center',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    View Certificate
                  </Link>
                ) : (
                  <div style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f5f5f5',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}>
                    {enrollment.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </div>
                )}

                <Link
                  to={`/competency/progress/${enrollment.id}`}
                  style={{
                    padding: '12px 20px',
                    background: '#fff',
                    color: '#D4AF37',
                    border: '2px solid #D4AF37',
                    borderRadius: '4px',
                    textAlign: 'center',
                    textDecoration: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  View Progress
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {enrollments.length > 0 && (
        <div style={{
          marginTop: '40px',
          padding: '20px',
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}>
          <h3 style={{ marginTop: 0 }}>Need Help?</h3>
          <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
            If you have questions about your courses or need support, please contact your assigned assessor
            or reach out to ICSC support.
          </p>
          <Link
            to="/tickets"
            style={{
              display: 'inline-block',
              color: '#D4AF37',
              textDecoration: 'underline'
            }}
          >
            Submit a Support Ticket
          </Link>
        </div>
      )}
    </div>
  );
};

export default CompetencyDashboard;
